import React, { useState, useEffect, useCallback } from "react";
import { getSupportEmail } from "../../utils/domainConfig";
import { useToast } from "../../contexts/ToastContext";

/**
 * Form mode determines field editability rules:
 * - create: all fields editable (PA-6, initial company data setup)
 * - edit-self-service: NIP read-only after set (PA-7, user self-service)
 * - edit-admin: all fields editable (PA-8, admin panel)
 */
export type PartyDataFormMode = "create" | "edit-self-service" | "edit-admin";

/**
 * Party/company data fields matching SelfServiceBillingDataResponse
 */
export interface PartyDataFormValues {
  companyName: string;
  nip: string;
  street: string;
  city: string;
  postalCode: string;
  country: string;
  billingEmail: string;
}

/** Data returned from GUS lookup */
export interface GusLookupData {
  companyName: string;
  street: string;
  city: string;
  postalCode: string;
  regon?: string;
}

interface PartyDataFormProps {
  /** Form mode determining field editability */
  mode: PartyDataFormMode;
  /** Initial form values (from GET endpoint or empty for create) */
  initialValues?: Partial<PartyDataFormValues>;
  /** Called on form submit with validated data */
  onSubmit: (values: PartyDataFormValues) => Promise<void>;
  /** Whether NIP was previously set (controls read-only in edit-self-service) */
  nipAlreadySet?: boolean;
  /** Support email for NIP change contact message */
  supportEmail?: string;
  /** Whether form submission is in progress */
  isSubmitting?: boolean;
  /** Whether rate limit has been hit (429 from POST) */
  rateLimitHit?: boolean;
  /** GUS lookup function — when provided, shows "Pobierz z GUS" button next to NIP */
  onGusLookup?: (nip: string) => Promise<GusLookupData>;
}

/** Polish postal code pattern: XX-XXX */
const POSTAL_CODE_REGEX = /^\d{2}-\d{3}$/;

/**
 * Strip optional "PL" prefix, spaces, and dashes from NIP.
 * Accepts: "PL5521724915", "PL 552-172-49-15", "552-172-49-15", "5521724915"
 * Returns: "5521724915"
 */
const normalizeNip = (nip: string): string =>
  nip.trim().toUpperCase().replace(/^PL/, '').replace(/[\s-]/g, '');

/** Validate normalized NIP — exactly 10 digits */
const isValidNip = (nip: string): boolean => /^\d{10}$/.test(normalizeNip(nip));

/** Email validation (basic, matches backend @IsEmail()) */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface FieldError {
  companyName?: string;
  nip?: string;
  street?: string;
  city?: string;
  postalCode?: string;
  billingEmail?: string;
}

/**
 * Shared company data form component used across billing UI.
 *
 * Created by PA-7 (user self-service), reused by:
 * - PA-6 (create mode, GUS pre-fill)
 * - PA-8 (admin edit mode, all fields editable)
 *
 * Follows the same form pattern as AccountSettings.tsx (plain React state).
 */
export const PartyDataForm: React.FC<PartyDataFormProps> = ({
  mode,
  initialValues,
  onSubmit,
  nipAlreadySet = false,
  supportEmail,
  isSubmitting = false,
  rateLimitHit = false,
  onGusLookup,
}) => {
  const resolvedSupportEmail = supportEmail || getSupportEmail();
  const { addToast } = useToast();

  const [values, setValues] = useState<PartyDataFormValues>({
    companyName: initialValues?.companyName || "",
    nip: initialValues?.nip || "",
    street: initialValues?.street || "",
    city: initialValues?.city || "",
    postalCode: initialValues?.postalCode || "",
    country: initialValues?.country || "PL",
    billingEmail: initialValues?.billingEmail || "",
  });

  const [errors, setErrors] = useState<FieldError>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isGusLoading, setIsGusLoading] = useState(false);

  // Sync form with new initialValues when data loads
  useEffect(() => {
    if (initialValues) {
      setValues({
        companyName: initialValues.companyName || "",
        nip: initialValues.nip || "",
        street: initialValues.street || "",
        city: initialValues.city || "",
        postalCode: initialValues.postalCode || "",
        country: initialValues.country || "PL",
        billingEmail: initialValues.billingEmail || "",
      });
    }
  }, [initialValues]);

  /**
   * Determines if NIP field should be read-only.
   * In edit-self-service mode, NIP becomes read-only once set.
   * In edit-admin mode, all fields are always editable.
   * In create mode, all fields are editable.
   */
  const isNipReadOnly =
    mode === "edit-self-service" && nipAlreadySet;

  /** Show GUS button only when NIP is editable and onGusLookup is provided */
  const showGusButton = !isNipReadOnly && !!onGusLookup;

  /** NIP has exactly 10 digits (after stripping optional PL prefix, dashes, spaces) */
  const nipIsValid = isValidNip(values.nip);

  const isGusButtonDisabled = !nipIsValid || isSubmitting || isGusLoading;

  const validate = useCallback(
    (fieldValues: PartyDataFormValues): FieldError => {
      const newErrors: FieldError = {};

      // companyName: required, max 255
      if (!fieldValues.companyName.trim()) {
        newErrors.companyName = "Nazwa firmy jest wymagana.";
      } else if (fieldValues.companyName.length > 255) {
        newErrors.companyName = "Nazwa firmy nie może przekraczać 255 znaków.";
      }

      // NIP: required if not already set (in edit-self-service) or in create mode
      if (!isNipReadOnly) {
        if (!fieldValues.nip.trim()) {
          newErrors.nip = "NIP jest wymagany.";
        } else if (!isValidNip(fieldValues.nip)) {
          newErrors.nip = "NIP musi składać się z 10 cyfr (opcjonalnie z przedrostkiem PL).";
        }
      }

      // street: required, max 100
      if (!fieldValues.street.trim()) {
        newErrors.street = "Ulica jest wymagana.";
      } else if (fieldValues.street.length > 100) {
        newErrors.street = "Ulica nie może przekraczać 100 znaków.";
      }

      // city: required, max 100
      if (!fieldValues.city.trim()) {
        newErrors.city = "Miasto jest wymagane.";
      } else if (fieldValues.city.length > 100) {
        newErrors.city = "Miasto nie może przekraczać 100 znaków.";
      }

      // postalCode: required, format XX-XXX
      if (!fieldValues.postalCode.trim()) {
        newErrors.postalCode = "Kod pocztowy jest wymagany.";
      } else if (!POSTAL_CODE_REGEX.test(fieldValues.postalCode.trim())) {
        newErrors.postalCode = "Kod pocztowy musi mieć format XX-XXX.";
      }

      // billingEmail: optional, but if provided must be valid email, max 255
      if (fieldValues.billingEmail.trim()) {
        if (!EMAIL_REGEX.test(fieldValues.billingEmail.trim())) {
          newErrors.billingEmail = "Podaj prawidłowy adres email.";
        } else if (fieldValues.billingEmail.length > 255) {
          newErrors.billingEmail = "Email nie może przekraczać 255 znaków.";
        }
      }

      return newErrors;
    },
    [isNipReadOnly]
  );

  const handleChange = (field: keyof PartyDataFormValues, value: string) => {
    setValues((prev) => ({ ...prev, [field]: value }));

    // Clear error for field on change if it was touched
    if (touched[field] && errors[field as keyof FieldError]) {
      const newErrors = validate({ ...values, [field]: value });
      setErrors(newErrors);
    }
  };

  const handleBlur = (field: keyof PartyDataFormValues) => {
    setTouched((prev) => ({ ...prev, [field]: true }));

    // Validate on blur
    const newErrors = validate(values);
    setErrors(newErrors);
  };

  const handleGusLookup = async () => {
    if (!onGusLookup || isGusButtonDisabled) return;

    setIsGusLoading(true);
    try {
      const gusData = await onGusLookup(normalizeNip(values.nip));
      setValues((prev) => ({
        ...prev,
        companyName: gusData.companyName,
        street: gusData.street,
        city: gusData.city,
        postalCode: gusData.postalCode,
      }));
      addToast({
        type: "success",
        message: "Dane z GUS zostały pobrane. Sprawdź i zapisz.",
      });
    } catch (err: unknown) {
      const axiosError = err as { response?: { status?: number } };
      const status = axiosError.response?.status;

      if (status === 404) {
        addToast({
          type: "error",
          message: "Nie znaleziono firmy o podanym NIP w rejestrze GUS.",
        });
      } else if (status === 429) {
        addToast({
          type: "error",
          message: "Przekroczono limit pobrań z GUS. Spróbuj za godzinę.",
        });
      } else {
        addToast({
          type: "error",
          message: "Nie udało się pobrać danych z GUS. Wypełnij formularz ręcznie.",
        });
      }
    } finally {
      setIsGusLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Mark all fields as touched
    const allTouched: Record<string, boolean> = {};
    Object.keys(values).forEach((key) => {
      allTouched[key] = true;
    });
    setTouched(allTouched);

    // Validate all fields
    const validationErrors = validate(values);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    // Trim values and strip NIP prefix before submitting
    const trimmedValues: PartyDataFormValues = {
      companyName: values.companyName.trim(),
      nip: normalizeNip(values.nip),
      street: values.street.trim(),
      city: values.city.trim(),
      postalCode: values.postalCode.trim(),
      country: values.country,
      billingEmail: values.billingEmail.trim(),
    };

    await onSubmit(trimmedValues);
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="company-form"
      noValidate
    >
      <div className="space-y-4">
        {/* NIP — first field (AC-F1) */}
        <div>
          <label
            htmlFor="nip"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            NIP{" "}
            {!isNipReadOnly && <span className="text-red-500">*</span>}
          </label>
          {/* NIP + GUS button wrapper (AC-F7) */}
          <div
            data-testid="nip-gus-wrapper"
            className={`flex flex-col${showGusButton ? " sm:flex-row sm:items-end sm:gap-2" : ""}`}
          >
            <input
              id="nip"
              type="text"
              value={values.nip}
              onChange={(e) => handleChange("nip", e.target.value)}
              onBlur={() => handleBlur("nip")}
              placeholder="np. 5521724915 lub PL5521724915"
              maxLength={20}
              disabled={isNipReadOnly}
              data-testid="nip-field"
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                isNipReadOnly
                  ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                  : ""
              } ${
                touched.nip && errors.nip
                  ? "border-red-300 focus:ring-red-500"
                  : "border-gray-300"
              }`}
              aria-invalid={touched.nip && !!errors.nip}
              aria-describedby={
                isNipReadOnly
                  ? "nip-helper"
                  : touched.nip && errors.nip
                    ? "nip-error"
                    : "nip-format-hint"
              }
            />
            {/* GUS button (AC-F2, AC-F3, AC-F4, AC-F8) */}
            {showGusButton && (
              <button
                type="button"
                data-testid="gus-lookup-btn"
                onClick={handleGusLookup}
                disabled={isGusButtonDisabled}
                className="mt-2 sm:mt-0 px-3 py-2 text-sm font-medium text-primary-800 bg-white border border-primary-300 rounded-lg hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center gap-1"
              >
                {isGusLoading ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4 text-primary-600"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v8H4z"
                      />
                    </svg>
                    Pobieranie...
                  </>
                ) : (
                  "Pobierz z GUS"
                )}
              </button>
            )}
          </div>
          {isNipReadOnly && (
            <p id="nip-helper" className="mt-1 text-sm text-gray-500">
              NIP nie może być zmieniony. Skontaktuj się z{" "}
              <a
                href={`mailto:${resolvedSupportEmail}`}
                className="text-primary-700 hover:underline"
              >
                {resolvedSupportEmail}
              </a>
              .
            </p>
          )}
          {/* GUS helper text when NIP not yet 10 digits (AC-F8) */}
          {showGusButton && !nipIsValid && (
            <p className="mt-1 text-sm text-gray-500">
              Wpisz poprawny NIP (10 cyfr, np. 5521724915 lub PL5521724915), aby pobrać dane z GUS.
            </p>
          )}
          {!isNipReadOnly && !showGusButton && !(touched.nip && errors.nip) && (
            <p id="nip-format-hint" className="mt-1 text-sm text-gray-500">
              10 cyfr, opcjonalnie z przedrostkiem PL.
            </p>
          )}
          {!isNipReadOnly && touched.nip && errors.nip && (
            <p
              id="nip-error"
              className="mt-1 text-sm text-red-600"
              role="alert"
            >
              {errors.nip}
            </p>
          )}
        </div>

        {/* Company Name */}
        <div>
          <label
            htmlFor="companyName"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Nazwa firmy <span className="text-red-500">*</span>
          </label>
          <input
            id="companyName"
            type="text"
            value={values.companyName}
            onChange={(e) => handleChange("companyName", e.target.value)}
            onBlur={() => handleBlur("companyName")}
            maxLength={255}
            className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              touched.companyName && errors.companyName
                ? "border-red-300 focus:ring-red-500"
                : "border-gray-300"
            }`}
            aria-invalid={touched.companyName && !!errors.companyName}
            aria-describedby={
              touched.companyName && errors.companyName
                ? "companyName-error"
                : undefined
            }
          />
          {touched.companyName && errors.companyName && (
            <p
              id="companyName-error"
              className="mt-1 text-sm text-red-600"
              role="alert"
            >
              {errors.companyName}
            </p>
          )}
        </div>

        {/* Street */}
        <div>
          <label
            htmlFor="street"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Ulica <span className="text-red-500">*</span>
          </label>
          <input
            id="street"
            type="text"
            value={values.street}
            onChange={(e) => handleChange("street", e.target.value)}
            onBlur={() => handleBlur("street")}
            maxLength={100}
            className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              touched.street && errors.street
                ? "border-red-300 focus:ring-red-500"
                : "border-gray-300"
            }`}
            aria-invalid={touched.street && !!errors.street}
            aria-describedby={
              touched.street && errors.street ? "street-error" : undefined
            }
          />
          {touched.street && errors.street && (
            <p
              id="street-error"
              className="mt-1 text-sm text-red-600"
              role="alert"
            >
              {errors.street}
            </p>
          )}
        </div>

        {/* City */}
        <div>
          <label
            htmlFor="city"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Miasto <span className="text-red-500">*</span>
          </label>
          <input
            id="city"
            type="text"
            value={values.city}
            onChange={(e) => handleChange("city", e.target.value)}
            onBlur={() => handleBlur("city")}
            maxLength={100}
            className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              touched.city && errors.city
                ? "border-red-300 focus:ring-red-500"
                : "border-gray-300"
            }`}
            aria-invalid={touched.city && !!errors.city}
            aria-describedby={
              touched.city && errors.city ? "city-error" : undefined
            }
          />
          {touched.city && errors.city && (
            <p
              id="city-error"
              className="mt-1 text-sm text-red-600"
              role="alert"
            >
              {errors.city}
            </p>
          )}
        </div>

        {/* Postal Code */}
        <div>
          <label
            htmlFor="postalCode"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Kod pocztowy <span className="text-red-500">*</span>
          </label>
          <input
            id="postalCode"
            type="text"
            value={values.postalCode}
            onChange={(e) => handleChange("postalCode", e.target.value)}
            onBlur={() => handleBlur("postalCode")}
            placeholder="XX-XXX"
            maxLength={6}
            className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              touched.postalCode && errors.postalCode
                ? "border-red-300 focus:ring-red-500"
                : "border-gray-300"
            }`}
            aria-invalid={touched.postalCode && !!errors.postalCode}
            aria-describedby={
              touched.postalCode && errors.postalCode
                ? "postalCode-error"
                : undefined
            }
          />
          {touched.postalCode && errors.postalCode && (
            <p
              id="postalCode-error"
              className="mt-1 text-sm text-red-600"
              role="alert"
            >
              {errors.postalCode}
            </p>
          )}
        </div>

        {/* Country (read-only) */}
        <div>
          <label
            htmlFor="country"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Kraj
          </label>
          <input
            id="country"
            type="text"
            value={values.country}
            disabled
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-100 text-gray-500 cursor-not-allowed"
            aria-describedby="country-helper"
          />
          <p id="country-helper" className="mt-1 text-sm text-gray-500">
            Obsługujemy firmy z siedzibą w Polsce.
          </p>
        </div>

        {/* Billing Email */}
        <div>
          <label
            htmlFor="billingEmail"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Email do faktur
          </label>
          <input
            id="billingEmail"
            type="email"
            value={values.billingEmail}
            onChange={(e) => handleChange("billingEmail", e.target.value)}
            onBlur={() => handleBlur("billingEmail")}
            maxLength={255}
            className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              touched.billingEmail && errors.billingEmail
                ? "border-red-300 focus:ring-red-500"
                : "border-gray-300"
            }`}
            aria-invalid={touched.billingEmail && !!errors.billingEmail}
            aria-describedby={
              touched.billingEmail && errors.billingEmail
                ? "billingEmail-error"
                : "billingEmail-helper"
            }
          />
          {touched.billingEmail && errors.billingEmail ? (
            <p
              id="billingEmail-error"
              className="mt-1 text-sm text-red-600"
              role="alert"
            >
              {errors.billingEmail}
            </p>
          ) : (
            <p id="billingEmail-helper" className="mt-1 text-sm text-gray-500">
              Jeśli puste, na fakturach pojawi się główny email konta z momentu
              złożenia zamówienia.
            </p>
          )}
        </div>

        {/* Rate limit message */}
        {rateLimitHit && (
          <div
            data-testid="rate-limit-message"
            className="p-3 bg-red-50 border border-red-200 rounded-lg"
            role="alert"
          >
            <p className="text-sm text-red-700">
              Zbyt wiele prób. Spróbuj za godzinę.
            </p>
          </div>
        )}

        {/* Submit button */}
        <div className="pt-2">
          <button
            type="submit"
            disabled={isSubmitting || rateLimitHit}
            data-testid="company-save"
            className="px-6 py-2 bg-primary-700 text-white font-medium rounded-lg hover:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-busy={isSubmitting}
          >
            {isSubmitting ? "Zapisywanie..." : "Zapisz"}
          </button>
        </div>
      </div>
    </form>
  );
};
